import React, { useMemo, useState } from 'react'
import styled from 'styled-components/macro'
import { ModalForm, ModalFormId } from 'src/components/ModalForms'
import { useHideModal, useIsShowModal } from 'src/Modal/ModalStateContext'
import { CsvDataGridContainer } from 'src/components/ModalForms/Evidence/evidence.styles'
import palette from 'src/designSystem/variables/palette'
import { EvidenceDataGrid } from 'src/pages/Evidence/show/tabs/evidence/AutomaticEvidence/AutomaticEvidenceBody'
import { getEvidenceCsv } from 'src/Utils/Evidence/evidence.helpers'
import { FlexAlign, FlexRow } from 'src/components/Reusable/Flex'
import { Tooltip } from 'src/components/Reusable/Tooltip'
import {
  TooltipOverlayType,
  TooltipPositions,
} from 'src/components/Reusable/Tooltip/Tooltip'
import { convertRowSecondsToISODate } from 'src/Utils/grid/grid.helpers'
import { useSearchableEvidenceInstance } from 'src/pages/Evidence/evidence.hooks'
import { useApplySearch } from 'src/Utils/search/search.hooks'
import FileSaver from 'file-saver'
import { PERMISSIONS } from 'src/config/roleConfig'
import { ExportIconButton } from 'src/components/Reusable/Buttons'
import { AutomaticEvidenceToolbarContainer } from 'src/pages/Evidence/show/tabs/evidence/AutomaticEvidence/AutomaticEvidenceBody.styles'
import { Search } from 'src/components/Reusable/Search/Search'
import { GridRowClassNameParams } from '@mui/x-data-grid'

const EmptyTabularBody = styled.div`
  padding: 24px 16px;
  font-style: italic;
  color: ${palette.neutral[600]};
`

export const TabularArtifactModal = ({
  formId,
  title,
  description,
  csv,
  headerTooltip,
  getRowClassName,
}: {
  formId: ModalFormId
  title: string
  description: JSX.Element
  csv: string
  headerTooltip?: {
    header: string
    tooltip: string
  }
  getRowClassName?: (params: GridRowClassNameParams) => string
}): JSX.Element => {
  const [isSearchExpanded, setIsSearchExpanded] = useState(false)
  const [tempSearchQuery, setTempSearchQuery] = useState<string | undefined>(
    undefined,
  )
  const show = useIsShowModal(formId)
  const hide = useHideModal({
    modalId: formId,
    onHide: () => {
      setTempSearchQuery(undefined)
      setIsSearchExpanded(false)
    },
  })

  const onClose = () => {
    hide()
  }

  const { rows: rowsToUse, columns } = getEvidenceCsv(csv, headerTooltip)
  const rows = useMemo(
    () => rowsToUse.map((row) => convertRowSecondsToISODate(row)),
    [rowsToUse],
  )
  const paramPrefix = 'artifact-csv'
  const searchableRows = useSearchableEvidenceInstance([...rows])
  const { data: matchingRows } = useApplySearch(
    searchableRows,
    paramPrefix,
    tempSearchQuery,
  )
  const handleSearchChange = (query: string) => {
    setTempSearchQuery(query)
  }
  const hasTable = columns.length > 0
  const downloadCsv = (csv: string) => {
    const table = new Blob([csv], {
      type: 'text/csv;charset=utf-8;',
    })
    FileSaver.saveAs(table, `${title}.csv`)
  }

  const truncatedTitle =
    title && title?.length > 50 ? (
      <Tooltip
        id={title}
        placement={TooltipPositions.bottom}
        overlayType={TooltipOverlayType.popover}
        tooltipBody={title}
      >
        <span>{title.slice(0, 50) + '...'}</span>
      </Tooltip>
    ) : (
      title
    )

  return (
    <ModalForm
      show={show}
      hide={onClose}
      formId={formId}
      title={truncatedTitle}
      description={description}
      hideButtons
    >
      <CsvDataGridContainer $isModal>
        {hasTable ? (
          <>
            <AutomaticEvidenceToolbarContainer>
              <FlexRow
                justify={FlexAlign.SPACE_BETWEEN}
                isFullWidth
                $wrap="wrap"
                gap={12}
              >
                <ExportIconButton
                  onClick={() => downloadCsv(csv)}
                  tooltipText="Export to CSV"
                  requiredPermissions={[PERMISSIONS.READ]}
                />
                <Search
                  isExpanded={isSearchExpanded}
                  paramPrefix={paramPrefix}
                  setIsExpanded={setIsSearchExpanded}
                  onChange={handleSearchChange}
                  disableEnter
                />
              </FlexRow>
            </AutomaticEvidenceToolbarContainer>
            <EvidenceDataGrid
              rows={matchingRows}
              columns={columns}
              getRowClassName={getRowClassName}
            />
          </>
        ) : (
          <EmptyTabularBody>No table to display.</EmptyTabularBody>
        )}
      </CsvDataGridContainer>
    </ModalForm>
  )
}
